import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { formatBRL } from "@/lib/format";
import { getProductImageUrl } from "@/lib/utils";
import { Plus, Minus, Trash2, ArrowRight, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function CartDrawer() {
  const { items, updateQty, removeItem, total, open, setOpen } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 bg-white border-l border-[#f1f1f3] flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="p-6 border-b border-[#f1f1f3]">
          <SheetTitle className="font-quicksand text-2xl font-extrabold text-[#3d3d3d] flex items-center gap-3">
            <ShoppingBag className="w-6 h-6 text-[#1BB0CE]" />
            Carrinho
            <span className="text-xs font-quicksand font-semibold text-[#999] ml-auto">
              {items.length} {items.length === 1 ? "item" : "itens"}
            </span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-gp-gradient-light mx-auto flex items-center justify-center mb-4">
                <ShoppingBag className="w-7 h-7 text-[#1BB0CE]" />
              </div>
              <p className="text-sm text-[#666]">Seu carrinho está vazio.</p>
              <button onClick={() => setOpen(false)} className="btn-gp mt-6" data-testid="cart-continue-empty">
                CONTINUAR COMPRANDO
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-[#f1f1f3]">
              {items.map((it) => (
                <li key={it.product_id} className="p-5 flex gap-4" data-testid={`cart-item-${it.product_id}`}>
                  <div className="w-20 h-20 bg-gp-gradient-light rounded-2xl flex-shrink-0 overflow-hidden">
                    <img src={getProductImageUrl(it.image_url)} alt={it.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-quicksand font-bold text-sm leading-tight text-[#3d3d3d]">{it.name}</h4>
                    <p className="text-xs text-[#666] mt-1">{formatBRL(it.price)}</p>
                    <div className="flex items-center gap-3 mt-3">
                      <div className="flex items-center border border-[#e4e4e7] rounded-full">
                        <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="p-1.5 hover:bg-[#f4f4f5] rounded-l-full" data-testid={`cart-decrease-${it.product_id}`}>
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-sm font-quicksand font-bold">{it.quantity}</span>
                        <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="p-1.5 hover:bg-[#f4f4f5] rounded-r-full" data-testid={`cart-increase-${it.product_id}`}>
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <button onClick={() => removeItem(it.product_id)} className="p-2 text-[#FF3B30] hover:bg-[#FFF1F0] rounded-full" data-testid={`cart-remove-${it.product_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="font-quicksand text-sm font-bold text-[#3d3d3d]">{formatBRL(it.price * it.quantity)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#f1f1f3] p-6 bg-white">
            <div className="flex justify-between items-baseline mb-4">
              <span className="text-xs uppercase tracking-wider font-quicksand font-bold text-[#666]">Total PIX</span>
              <span className="font-quicksand text-2xl font-extrabold text-gradient-gp" data-testid="cart-total">
                {formatBRL(total)}
              </span>
            </div>
            <button onClick={goCheckout} data-testid="cart-checkout-button" className="btn-gp w-full">
              FINALIZAR PEDIDO <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
